import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Spinner - Loading indicator
 * 
 * Standalone loading spinner (różny od loading state w Button)
 * - Rozmiary: "1" | "2" | "3" | "4"
 * - Color support through CSS variables
 * 
 * @example
 * ```tsx
 * <Spinner />
 * <Spinner size="3" className="text-[var(--pink-9)]" />
 * ```
 */

const spinnerVariants = cva(
  cn(
    "inline-block animate-spin",
    "border-2 border-solid rounded-full",
    "border-current border-t-transparent"
  ),
  {
    variants: {
      size: {
        "1": "h-[16px] w-[16px] border-[2px]",
        "2": "h-[20px] w-[20px] border-[2px]",
        "3": "h-[24px] w-[24px] border-[2px]",
        "4": "h-[32px] w-[32px] border-[3px]",
      },
    },
    defaultVariants: {
      size: "2",
    },
  }
)

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  /**
   * Label for screen readers
   * @default "Ładowanie..."
   */
  label?: string
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, label = "Ładowanie...", ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label}
        className={cn(spinnerVariants({ size }), className)}
        {...props}
      >
        <span className="sr-only">{label}</span>
      </div>
    )
  }
)
Spinner.displayName = "Spinner"

export { Spinner, spinnerVariants }

